import { FileUploadError } from "./file-upload";

export interface ChunkedUploadOptions {
  file: File;
  sasUrl: string;
  blockSize?: number; // Default: 2MB
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload file to Azure Blob Storage using chunked upload for better reliability
 * Uses Azure BlockBlob API with separate chunk uploads and final commit
 */
export async function uploadFileInChunks({
  file,
  sasUrl,
  blockSize = 2 * 1024 * 1024, // 2MB default
  onProgress,
  signal,
}: ChunkedUploadOptions): Promise<void> {
  const blockIds: string[] = [];
  const totalBlocks = Math.ceil(file.size / blockSize);
  let uploadedBlocks = 0;

  // Upload each chunk
  for (let blockNum = 0; blockNum < totalBlocks; blockNum++) {
    // Check for abort signal
    if (signal?.aborted) {
      throw new FileUploadError("Upload aborted", "ABORT_ERROR");
    }

    const start = blockNum * blockSize;
    const end = Math.min(start + blockSize, file.size);
    const block = file.slice(start, end);

    // Create base64-encoded block ID (Azure requires specific format)
    const blockIdString = `block-${String(blockNum).padStart(6, "0")}`;
    const blockId = btoa(blockIdString);
    // console.log(`[ChunkedUpload] Uploading block ${blockNum} with ID: ${blockId} (from: ${blockIdString})`);
    blockIds.push(blockId);

    // Upload individual block with per-chunk progress tracking
    await uploadBlock(sasUrl, blockId, block, signal, (chunkProgress) => {
      if (onProgress) {
        // Calculate overall progress: completed blocks + current block progress
        const completedBlocksProgress = (uploadedBlocks / totalBlocks) * 95;
        const currentBlockProgress = (chunkProgress / 100) * (95 / totalBlocks);
        const totalProgress = Math.round(completedBlocksProgress + currentBlockProgress);
        onProgress(Math.min(totalProgress, 95)); // Reserve 5% for commit
      }
    });

    uploadedBlocks++;

    // Report progress after each completed block
    if (onProgress) {
      const progress = Math.round((uploadedBlocks / totalBlocks) * 95);
      onProgress(Math.min(progress, 95));
    }
  }

  // Commit all blocks
  await commitBlockList(sasUrl, blockIds, signal);

  // Final progress update
  if (onProgress) {
    onProgress(100);
  }
}

/**
 * Upload a single block to Azure Storage with progress tracking
 */
async function uploadBlock(
  sasUrl: string,
  blockId: string,
  block: Blob,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
): Promise<void> {
  // Properly construct URL with chunked upload parameters
  const url = new URL(sasUrl);
  url.searchParams.set("comp", "block");
  url.searchParams.set("blockid", blockId);
  const uploadUrl = url.toString();

  const maxRetries = 5;
  let attempt = 0;

  const attemptUpload = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Handle abort signal
      if (signal) {
        signal.addEventListener("abort", () => {
          xhr.abort();
          reject(new FileUploadError("Upload aborted", "ABORT_ERROR"));
        });
      }

      // Track upload progress for this chunk
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.addEventListener("timeout", () => {
        reject(new Error("Upload timed out"));
      });

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
      xhr.timeout = 2 * 60000; // 120 second timeout per chunk
      xhr.send(block);
    });
  };

  while (attempt < maxRetries) {
    try {
      await attemptUpload();
      return; // Success
    } catch (error) {
      attempt++;

      // Don't retry on abort
      if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        throw new FileUploadError("Upload aborted", "ABORT_ERROR");
      }

      // Don't retry on client errors (4xx)
      if (error instanceof Error && error.message.includes("HTTP 4")) {
        throw new FileUploadError(`Upload failed: ${error.message}`, "UPLOAD_ERROR", error);
      }

      // Retry on network/server errors if attempts remain
      if (attempt >= maxRetries) {
        throw new FileUploadError(
          `Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`,
          "NETWORK_ERROR",
          error instanceof Error ? error : undefined,
        );
      }

      // Wait before retry (exponential backoff)
      await new Promise((resolve) => setTimeout(resolve, 2000 * Math.pow(2, attempt - 1)));
    }
  }
}

/**
 * Commit the uploaded blocks to finalize the blob
 */
async function commitBlockList(sasUrl: string, blockIds: string[], signal?: AbortSignal): Promise<void> {
  // Properly construct URL with commit parameters
  const url = new URL(sasUrl);
  url.searchParams.set("comp", "blocklist");
  const commitUrl = url.toString();

  const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
<BlockList>
${blockIds.map((id) => `  <Latest>${id}</Latest>`).join("\n")}
</BlockList>`;

  // console.log("[ChunkedUpload] Committing block list with", blockIds.length, "blocks");
  // console.log("[ChunkedUpload] Block IDs being committed:", blockIds);
  // console.log("[ChunkedUpload] Block list XML:", blockListXml);

  const maxRetries = 3;
  let attempt = 0;

  const attemptCommit = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Handle abort signal
      if (signal) {
        signal.addEventListener("abort", () => {
          xhr.abort();
          reject(new FileUploadError("Upload aborted", "ABORT_ERROR"));
        });
      }

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // console.log("[ChunkedUpload] Block list committed successfully");
          resolve();
        } else {
          // console.error("[ChunkedUpload] Commit failed:", xhr.status, xhr.statusText, xhr.responseText);
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during block list commit"));
      });

      xhr.addEventListener("timeout", () => {
        reject(new Error("Block list commit timed out"));
      });

      xhr.open("PUT", commitUrl);

      xhr.setRequestHeader("Content-Type", "application/xml; charset=utf-8");
      xhr.timeout = 30000; // 30 second timeout for commit
      xhr.send(blockListXml);
    });
  };

  while (attempt < maxRetries) {
    try {
      await attemptCommit();
      return; // Success
    } catch (error) {
      attempt++;

      // Don't retry on abort
      if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        throw new FileUploadError("Upload aborted", "ABORT_ERROR");
      }

      // Don't retry on client errors (4xx)
      if (error instanceof Error && error.message.includes("HTTP 4")) {
        throw new FileUploadError(`Block list commit failed: ${error.message}`, "UPLOAD_ERROR", error);
      }

      // Retry on network/server errors if attempts remain
      if (attempt >= maxRetries) {
        throw new FileUploadError(
          `Block list commit failed after ${maxRetries} attempts: ${error instanceof Error ? error.message : "Unknown error"}`,
          "NETWORK_ERROR",
          error instanceof Error ? error : undefined,
        );
      }

      // Wait before retry (exponential backoff)
      await new Promise((resolve) => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
    }
  }
}
